import React, { useState } from 'react';
import { Item } from '../lib/schema';
import { identifyCard, generateDescription, estimatePrice } from '../lib/ai';
// Generate a simple UUID
const generateId = () => Math.random().toString(36).substr(2, 9);

interface UploadTwoImagesProps {
  onResult: (item: Item) => void;
}

const UploadTwoImages: React.FC<UploadTwoImagesProps> = ({ onResult }) => {
  const [images, setImages] = useState<[File | null, File | null]>([null, null]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [hints, setHints] = useState('');

  const handleImageUpload = (index: 0 | 1, file: File) => {
    const newImages: [File | null, File | null] = [...images];
    newImages[index] = file;
    setImages(newImages);
  };

  const handleIdentify = async () => {
    if (!images[0] || !images[1]) {
      alert('Please upload both images');
      return;
    }

    setLoading(true);
    try {
      // Simulate AI identification
      const identification = await identifyCard(hints);
      
      // Generate description and price
      const description = await generateDescription(
        identification.name, 
        identification.set, 
        identification.condition
      );
      
      const priceEstimate = await estimatePrice(
        identification.name,
        identification.set,
        identification.condition
      );

      setResult({
        ...identification,
        description,
        priceEstimate
      });
    } catch (error) {
      console.error('Identification failed:', error);
      alert('Failed to identify card. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = () => {
    if (!result) return;

    const newItem: Item = {
      id: generateId(),
      name: result.name,
      set: result.set,
      condition: result.condition as any,
      images: [images[0]!.name, images[1]!.name],
      qty: 1,
      buyPrice: 0, // Will be filled by user
      buyDate: new Date().toISOString().split('T')[0],
      suggestedPrice: result.priceEstimate?.estimatedPrice,
      traderaTitle: `${result.name} – ${result.set || "Pokémon"} (${result.condition || "Okänd kondition"})`,
      traderaDesc: result.description
    };

    onResult(newItem);
    
    // Reset form
    setImages([null, null]);
    setResult(null);
    setHints('');
  };

  return (
    <div className="space-y-6">
      <div className="glass-effect p-6">
        <h2 className="text-2xl font-bold text-white mb-6">Identify & Buy Cards</h2>
        
        {/* Image Upload Areas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {[0, 1].map(index => (
            <div key={index} className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(index as 0 | 1, file);
                }}
                className="hidden"
                id={`upload-${index}`}
              />
              <label
                htmlFor={`upload-${index}`}
                className="cursor-pointer block"
              >
                {images[index] ? (
                  <div className="space-y-2">
                    <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-white font-medium">{images[index]!.name}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="w-16 h-16 mx-auto bg-gray-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <p className="text-gray-400">Click to upload {index === 0 ? 'Front of card' : 'Back of card'}</p>
                  </div>
                )}
              </label>
            </div>
          ))}
        </div>

        {/* Hints Input */}
        <div className="mb-6">
          <label className="block text-white font-medium mb-2">Additional Hints (Optional)</label>
          <input
            type="text"
            value={hints}
            onChange={(e) => setHints(e.target.value)}
            placeholder="e.g., 'Base Set Charizard, holo, some whitening on edges'"
            className="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pokemon-purple"
          />
        </div>

        {/* Identify Button */}
        <button
          onClick={handleIdentify}
          disabled={loading || !images[0] || !images[1]}
          className="w-full bg-pokemon-purple text-white py-3 px-6 rounded-lg font-medium hover:bg-pokemon-purple/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Identifying...' : 'Identify Card'}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="glass-effect p-6">
          <h3 className="text-xl font-bold text-white mb-4">Identification Results</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-sm">Card Name</label>
                  <p className="text-white font-medium">{result.name}</p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Set</label>
                  <p className="text-white font-medium">{result.set || 'Unknown'}</p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Condition</label>
                  <p className="text-white font-medium">{result.condition || 'Unknown'}</p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Confidence</label>
                  <p className="text-white font-medium">{(result.confidence * 100).toFixed(1)}%</p>
                </div>
              </div>
            </div>
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-sm">Estimated Price</label>
                  <p className="text-white font-medium">{result.priceEstimate?.estimatedPrice} SEK</p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Tradera Title</label>
                  <p className="text-white font-medium text-sm">{result.traderaTitle}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <label className="text-gray-400 text-sm block mb-2">Generated Description</label>
            <div className="bg-white/5 p-4 rounded-lg">
              <p className="text-white text-sm">{result.description}</p>
            </div>
          </div>

          <div className="mt-6 flex space-x-4">
            <button
              onClick={handleBuy}
              className="bg-green-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Add to Inventory
            </button>
            <button
              onClick={() => setResult(null)}
              className="bg-gray-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadTwoImages;
